import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import { handleTitleCaseChange } from '../utils/textUtils';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';
import { ValidationRules, validateForm, hasErrors } from '../utils/validation';
import ValidatedFormControl from '../components/ValidatedFormControl';
import ApplyRateModal from '../components/ApplyRateModal';

const BULK_FINANCIAL_FIELDS = [
  'sale_price',
  'week_rate',
  'one_day_rate',
  'two_day_rate',
  'three_day_rate',
  'four_day_rate',
  'part_day_rate',
  'weekend_rate',
  'hour_rate',
  'day_rate',
  'month_rate',
  'sc_rate',
  'unit_rate',
  'switch_week_rate',
  'deposit',
  'purch_price',
  'avg_purch_price',
];

function createEmptyHireStockBulkForm(defaultCategory = '') {
  return {
    cat: defaultCategory,
    stock_no: '',
    name: '',
    sale_price: undefined,
    additional_info: '',
    week_rate: undefined,
    one_day_rate: undefined,
    two_day_rate: undefined,
    three_day_rate: undefined,
    four_day_rate: undefined,
    part_day_rate: undefined,
    weekend_rate: undefined,
    hour_rate: undefined,
    day_rate: undefined,
    month_rate: undefined,
    sc_rate: undefined,
    unit_rate: undefined,
    switch_week_rate: undefined,
    switch_after_weeks: '',
    deposit: undefined,
    hire_notes: '',
    weight: '',
    purch_price: undefined,
    avg_purch_price: undefined,
    avg_purch_date: '',
    provisional_type: '',
    prop1: '',
    prop2: '',
    prop3: '',
    prop4: '',
    prop5: '',
    prop6: '',
    prop7: '',
    prop8: '',
    prop9: '',
    prop10: '',
    active: true,
  };
}

function normalizeHireStockBulkFormData(data) {
  const cleaned = { ...createEmptyHireStockBulkForm(), ...data };
  BULK_FINANCIAL_FIELDS.forEach((field) => {
    if (cleaned[field] === 0 || cleaned[field] === '0' || cleaned[field] === '0.00') {
      cleaned[field] = undefined;
    } else if (cleaned[field] != null && cleaned[field] !== '') {
      cleaned[field] = String(cleaned[field]);
    }
  });
  ['weight', 'switch_after_weeks', 'avg_purch_date'].forEach((field) => {
    if (cleaned[field] == null) {
      cleaned[field] = '';
    } else {
      cleaned[field] = String(cleaned[field]);
    }
  });
  if (cleaned.active == null) {
    cleaned.active = true;
  }
  return cleaned;
}

function HireStockBulkModal({ hireStockBulk, onClose, onSave, defaultCategory = '' }) {
  const [formData, setFormData] = useState(() => createEmptyHireStockBulkForm(defaultCategory));
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [hireCategories, setHireCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadRecord = async () => {
      if (!hireStockBulk?.id) {
        setFormData(createEmptyHireStockBulkForm(defaultCategory));
        setLoadError('');
        setLoadingRecord(false);
        return;
      }

      setLoadingRecord(true);
      setLoadError('');
      try {
        const response = await axios.get(`/api/hire-stock-bulk/${hireStockBulk.id}`);
        if (!cancelled) {
          setFormData(normalizeHireStockBulkFormData(response.data));
        }
      } catch (error) {
        console.error('Error loading hire stock bulk item:', error);
        if (!cancelled) {
          setLoadError('Failed to load hire stock bulk item');
          setFormData(normalizeHireStockBulkFormData(hireStockBulk));
        }
      } finally {
        if (!cancelled) {
          setLoadingRecord(false);
        }
      }
    };

    loadRecord();
    fetchHireCategories();

    return () => {
      cancelled = true;
    };
  }, [hireStockBulk, defaultCategory]);

  const fetchHireCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/hire-categories?limit=1000', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHireCategories(response.data.hireCategories || []);
    } catch (error) {
      console.error('Error fetching hire categories:', error);
    }
  };

  const titleCaseFields = ['name', 'additional_info', 'hire_notes', 'provisional_type'];

  const handleChange = (e) => {
    handleTitleCaseChange(e, setFormData, formData, titleCaseFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const errors = validateForm(formData, ValidationRules.hireStockBulk);
    setValidationErrors(errors);

    // If there are validation errors, don't submit
    if (hasErrors(errors)) {
      return;
    }

    setSaveError('');
    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save hire stock bulk item';
      setSaveError(message);
      const lower = message.toLowerCase();
      if (lower.includes('stock number') || lower.includes('item code')) {
        setValidationErrors({ ...errors, stock_no: message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {hireStockBulk ? 'Edit Hire Stock Bulk' : 'Add New Hire Stock Bulk'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loadError && (
            <div className="alert alert-warning py-2" role="alert">
              {loadError}
            </div>
          )}
          {saveError && (
            <div className="alert alert-danger py-2" role="alert">
              {saveError}
            </div>
          )}
          {loadingRecord ? (
            <div className="text-center py-5 text-muted">Loading item...</div>
          ) : (
            <Tabs defaultActiveKey="basic" id="hire-stock-bulk-tabs" className="mb-3">
              {/* Basic Information Tab */}
              <Tab eventKey="basic" title="Basic Info">
                <Container fluid>
                  <Row className="mb-3">
                    <Col md={4}>
                      <ValidatedFormControl
                        name="cat"
                        label="Category"
                        required={true}
                        validationErrors={validationErrors}
                        formData={formData}
                        handleChange={handleChange}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('cat')}>
                              Used in equipment classification, pricing, and hire contract
                              management
                            </Tooltip>
                          }
                        >
                          <Form.Select
                            name="cat"
                            value={formData.cat}
                            onChange={handleChange}
                            {...createTooltipProps('cat')}
                            isInvalid={!!validationErrors.cat}
                          >
                            <option value="">Select Category</option>
                            <MissingSelectOption
                              value={formData.cat}
                              options={hireCategories}
                              getOptionValue={(category) => category.code}
                            />
                            {hireCategories.map((category) => (
                              <option key={category.id} value={category.code}>
                                {category.name}
                              </option>
                            ))}
                          </Form.Select>
                        </OverlayTrigger>
                      </ValidatedFormControl>
                    </Col>
                    <Col md={4}>
                      <ValidatedFormControl
                        name="stock_no"
                        label="Stock No"
                        required={true}
                        validationErrors={validationErrors}
                        formData={formData}
                        handleChange={handleChange}
                        type="text"
                        maxLength="20"
                        {...createTooltipProps('stock_no')}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('stock_no')}>
                              Used for hire contract creation, availability tracking, and equipment
                              management
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="stock_no"
                            value={formData.stock_no}
                            onChange={handleChange}
                            maxLength="20"
                            {...createTooltipProps('stock_no')}
                            isInvalid={!!validationErrors.stock_no}
                          />
                        </OverlayTrigger>
                      </ValidatedFormControl>
                    </Col>
                    <Col md={4}>
                      <ValidatedFormControl
                        name="name"
                        label="Name"
                        required={true}
                        validationErrors={validationErrors}
                        formData={formData}
                        handleChange={handleChange}
                        type="text"
                        maxLength="120"
                        {...createTooltipProps('name')}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('name')}>
                              Used in equipment selection, contracts, and customer-facing documents
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            maxLength="120"
                            {...createTooltipProps('name')}
                            isInvalid={!!validationErrors.name}
                          />
                        </OverlayTrigger>
                      </ValidatedFormControl>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Additional Info</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('additional_info')}>
                              Stores additional equipment specifications, features, and custom data
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="additional_info"
                            value={formData.additional_info}
                            onChange={handleChange}
                            {...createTooltipProps('additional_info')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Hire Notes</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('hire_notes')}>
                              Additional notes specific to hire operations
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="hire_notes"
                            value={formData.hire_notes}
                            onChange={handleChange}
                            {...createTooltipProps('hire_notes')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Weight</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('weight')}>
                              Equipment weight for transport and handling purposes
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="weight"
                            value={formData.weight || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('weight')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Provisional Type</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('provisional_type')}>
                              Provisional equipment type
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="provisional_type"
                            value={formData.provisional_type}
                            onChange={handleChange}
                            {...createTooltipProps('provisional_type')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Active</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('active')}>
                              Controls whether equipment is available for hire
                            </Tooltip>
                          }
                        >
                          <Form.Check
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                            {...createTooltipProps('active')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* Pricing Tab */}
              <Tab eventKey="pricing" title="Pricing">
                <Container fluid>
                  {/* Standard Rates Row */}
                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Week Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('week_rate')}>
                              Used for longer-term hire contracts and pricing options
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="week_rate"
                            value={formData.week_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('week_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>One Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('one_day_rate')}>
                              Used in hire contract pricing and revenue calculations
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="one_day_rate"
                            value={formData.one_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('one_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Two Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('two_day_rate')}>
                              Two-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="two_day_rate"
                            value={formData.two_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('two_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Three Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('three_day_rate')}>
                              Three-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="three_day_rate"
                            value={formData.three_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('three_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Four Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('four_day_rate')}>
                              Four-day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="four_day_rate"
                            value={formData.four_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('four_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('day_rate')}>
                              Standard daily hire rate for the equipment
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="day_rate"
                            value={formData.day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Additional Rates Row */}
                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Part Day Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('part_day_rate')}>
                              Partial day hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="part_day_rate"
                            value={formData.part_day_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('part_day_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Weekend Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('weekend_rate')}>
                              Weekend hire rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="weekend_rate"
                            value={formData.weekend_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('weekend_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Hour Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('hour_rate')}>Hourly hire rate</Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="hour_rate"
                            value={formData.hour_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('hour_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Month Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('month_rate')}>
                              Used for monthly hire contracts and pricing options
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="month_rate"
                            value={formData.month_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('month_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>SC Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('sc_rate')}>
                              Special contract rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="sc_rate"
                            value={formData.sc_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('sc_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Unit Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('unit_rate')}>
                              Unit-based pricing rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="unit_rate"
                            value={formData.unit_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('unit_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Switch Rates and Deposit Row */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Switch Week Rate</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('switch_week_rate')}>
                              Rate after switching to weekly pricing
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="switch_week_rate"
                            value={formData.switch_week_rate || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('switch_week_rate')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Switch After Weeks</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('switch_after_weeks')}>
                              Number of weeks before switching to weekly rate
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="switch_after_weeks"
                            value={formData.switch_after_weeks || ''}
                            onChange={handleChange}
                            {...createTooltipProps('switch_after_weeks')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Deposit</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('deposit')}>
                              Security deposit required for equipment hire
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="deposit"
                            value={formData.deposit || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('deposit')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sale Price</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('sale_price')}>
                              Selling price for the equipment
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="sale_price"
                            value={formData.sale_price || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('sale_price')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* Purchase Information Tab */}
              <Tab eventKey="purchase" title="Purchase Info">
                <Container fluid>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Purchase Price</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('purch_price')}>
                              Used for depreciation calculations, insurance, and asset valuation
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="purch_price"
                            value={formData.purch_price || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('purch_price')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Avg Purchase Price</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('avg_purch_price')}>
                              Average purchase price for bulk items
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="number"
                            name="avg_purch_price"
                            value={formData.avg_purch_price || ''}
                            onChange={handleChange}
                            step="0.01"
                            {...createTooltipProps('avg_purch_price')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Avg Purchase Date</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('avg_purch_date')}>
                              Average purchase date for bulk items
                            </Tooltip>
                          }
                        >
                          <Form.Control
                            type="date"
                            name="avg_purch_date"
                            value={formData.avg_purch_date}
                            onChange={handleChange}
                            {...createTooltipProps('avg_purch_date')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>

              {/* Properties Tab */}
              <Tab eventKey="properties" title="Properties">
                <Container fluid>
                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop1</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop1')}>Custom property 1</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop1"
                            value={formData.prop1}
                            onChange={handleChange}
                            {...createTooltipProps('prop1')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop2</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop2')}>Custom property 2</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop2"
                            value={formData.prop2}
                            onChange={handleChange}
                            {...createTooltipProps('prop2')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop3</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop3')}>Custom property 3</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop3"
                            value={formData.prop3}
                            onChange={handleChange}
                            {...createTooltipProps('prop3')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop4</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop4')}>Custom property 4</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop4"
                            value={formData.prop4}
                            onChange={handleChange}
                            {...createTooltipProps('prop4')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop5</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop5')}>Custom property 5</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop5"
                            value={formData.prop5}
                            onChange={handleChange}
                            {...createTooltipProps('prop5')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop6</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop6')}>Custom property 6</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop6"
                            value={formData.prop6}
                            onChange={handleChange}
                            {...createTooltipProps('prop6')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop7</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop7')}>Custom property 7</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop7"
                            value={formData.prop7}
                            onChange={handleChange}
                            {...createTooltipProps('prop7')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop8</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop8')}>Custom property 8</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop8"
                            value={formData.prop8}
                            onChange={handleChange}
                            {...createTooltipProps('prop8')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop9</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop9')}>Custom property 9</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop9"
                            value={formData.prop9}
                            onChange={handleChange}
                            {...createTooltipProps('prop9')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prop10</Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('prop10')}>Custom property 10</Tooltip>
                          }
                        >
                          <Form.Control
                            type="text"
                            name="prop10"
                            value={formData.prop10}
                            onChange={handleChange}
                            {...createTooltipProps('prop10')}
                          />
                        </OverlayTrigger>
                      </Form.Group>
                    </Col>
                  </Row>
                </Container>
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loadingRecord || !!loadError || saving}>
            {saving ? 'Saving...' : hireStockBulk ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function HireStockBulk() {
  const [hireStockBulk, setHireStockBulk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHireStockBulk, setEditingHireStockBulk] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hireCategories, setHireCategories] = useState([]);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showApplyRateModal, setShowApplyRateModal] = useState(false);
  const [applyRateItems, setApplyRateItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchHireStockBulk = async (page = 1, search = '', category = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-stock-bulk', {
        params: {
          page,
          limit: pagination.limit,
          search,
          category,
        },
      });
      setHireStockBulk(response.data.hireStockBulk);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch hire stock bulk items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHireStockBulk(1, '', selectedCategory);
    fetchHireCategories();
  }, []);

  const fetchHireCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/hire-categories?limit=1000', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHireCategories(response.data.hireCategories || []);
    } catch (error) {
      console.error('Error fetching hire categories:', error);
    }
  };

  const getCategoryName = (categoryCode) => {
    const category = hireCategories.find((cat) => cat.code === categoryCode);
    return category ? category.name : categoryCode;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHireStockBulk(1, searchTerm, selectedCategory);
  };

  const handlePageChange = (newPage) => {
    fetchHireStockBulk(newPage, searchTerm, selectedCategory);
  };

  const handleCategoryFilter = (categoryCode) => {
    setSelectedCategory(categoryCode);
    fetchHireStockBulk(1, searchTerm, categoryCode);
  };

  const handleAddHireStockBulk = () => {
    setEditingHireStockBulk(null);
    setShowModal(true);
  };

  const handleApplyRateClick = async () => {
    if (!selectedCategory) {
      setApplyRateItems([]);
      setShowApplyRateModal(true);
      return;
    }
    try {
      const response = await axios.get('/api/hire-stock-bulk', {
        params: { page: 1, limit: 10000, category: selectedCategory },
      });
      setApplyRateItems(response.data.hireStockBulk || []);
      setShowApplyRateModal(true);
    } catch (err) {
      setError('Failed to load items for apply rate');
    }
  };

  const handleEditHireStockBulk = (hireStockBulkItem) => {
    setEditingHireStockBulk(hireStockBulkItem);
    setShowModal(true);
  };

  const handleSaveHireStockBulk = async (hireStockBulkData) => {
    if (editingHireStockBulk) {
      const response = await axios.put(
        `/api/hire-stock-bulk/${editingHireStockBulk.id}`,
        hireStockBulkData
      );
      let successMessage = 'Hire stock bulk item updated successfully';

      // Check if cascade updates occurred
      if (response.data.cascadeUpdates) {
        const { inclusions, fleetReminders } = response.data.cascadeUpdates;
        const totalUpdates = (inclusions || 0) + (fleetReminders || 0);

        if (totalUpdates > 0) {
          const parts = [];
          if (inclusions > 0) parts.push(`${inclusions} inclusion${inclusions !== 1 ? 's' : ''}`);
          if (fleetReminders > 0)
            parts.push(`${fleetReminders} fleet reminder${fleetReminders !== 1 ? 's' : ''}`);
          successMessage += `. Also updated ${parts.join(' and ')} with the new stock number.`;
        }
      }

      setSuccess(successMessage);
    } else {
      await axios.post('/api/hire-stock-bulk', hireStockBulkData);
      setSuccess('Hire stock bulk item created successfully');
    }
    setShowModal(false);
    fetchHireStockBulk(pagination.page, searchTerm, selectedCategory);
  };

  const handleDeleteHireStockBulk = async (id) => {
    if (window.confirm('Are you sure you want to delete this hire stock bulk item?')) {
      try {
        await axios.delete(`/api/hire-stock-bulk/${id}`);
        setSuccess('Hire stock bulk item deleted successfully');
        fetchHireStockBulk(pagination.page, searchTerm, selectedCategory);
      } catch (error) {
        setError('Failed to delete hire stock bulk item');
      }
    }
  };

  const handleCopyHireStockBulk = async (hireStockBulkItem) => {
    setCopyingId(hireStockBulkItem.id);
    try {
      const copiedData = { ...hireStockBulkItem };
      delete copiedData.id;
      delete copiedData.created_at;
      delete copiedData.updated_at;

      const originalStockNo = copiedData.stock_no;
      const match = originalStockNo.match(/^(.*?)(\d+)$/);
      const baseStockNo = match ? match[1] : originalStockNo;
      const originalPadLength = match ? match[2].length : 2;

      const existingNumbers = hireStockBulk
        .map((item) => {
          const itemMatch = item.stock_no.match(
            new RegExp(`^${baseStockNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`)
          );
          return itemMatch ? parseInt(itemMatch[1]) : 0;
        })
        .filter((num) => num > 0);

      const highestNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
      const newNumber = highestNum + 1;
      copiedData.stock_no = `${baseStockNo}${String(newNumber).padStart(originalPadLength, '0')}`;

      const token = localStorage.getItem('token');
      const newStockNo = copiedData.stock_no;

      let inclusionsCopied = 0;
      let inclusionsFound = 0;
      let inclusionsFailed = 0;
      let inclusionError = null;

      await wrapCopy('Copying hire stock bulk item...', 1, async (tick) => {
        await axios.post('/api/hire-stock-bulk', copiedData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        try {
          const inclusionsResponse = await axios.get(
            `/api/inclusions/by-parent/${encodeURIComponent(originalStockNo)}`,
            {
              params: { parentType: 'S' },
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const stockInclusions = inclusionsResponse.data || [];
          inclusionsFound = stockInclusions.length;

          for (const inclusion of stockInclusions) {
            try {
              const newInclusion = { ...inclusion };
              delete newInclusion.id;
              delete newInclusion.created_at;
              delete newInclusion.updated_at;
              delete newInclusion.user_id;
              newInclusion.parent_code = newStockNo;

              await axios.post('/api/inclusions', newInclusion, {
                headers: { Authorization: `Bearer ${token}` },
              });
              inclusionsCopied++;
            } catch (singleError) {
              console.warn(
                'Failed to copy inclusion:',
                inclusion.incl_cat,
                singleError.response?.data?.error
              );
              inclusionsFailed++;
              if (!inclusionError) {
                inclusionError = singleError.response?.data?.error || singleError.message;
              }
            }
          }
        } catch (inclError) {
          console.error('Error fetching inclusions:', inclError);
          inclusionError = inclError.response?.data?.error || inclError.message;
        }

        tick({ success: true });
      });

      let message = 'Hire stock bulk item copied successfully';
      if (inclusionsCopied > 0 && inclusionsFailed === 0) {
        message += ` (${inclusionsCopied} inclusion${inclusionsCopied > 1 ? 's' : ''} also copied)`;
      } else if (inclusionsCopied > 0 && inclusionsFailed > 0) {
        message += ` (${inclusionsCopied} inclusion${inclusionsCopied > 1 ? 's' : ''} copied, ${inclusionsFailed} skipped due to missing categories)`;
      } else if (inclusionsFound > 0 && inclusionsFailed > 0) {
        message += ` (${inclusionsFailed} inclusion${inclusionsFailed > 1 ? 's' : ''} skipped - missing categories)`;
      } else if (inclusionError) {
        message += ` (Inclusion error: ${inclusionError})`;
      }
      setSuccess(message);
      fetchHireStockBulk(pagination.page, searchTerm, selectedCategory);
    } catch (error) {
      setError('Failed to copy hire stock bulk item');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDeleteAllHireStockBulk = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL hire stock bulk items? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your hire stock bulk records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/hire-stock-bulk');
          setSuccess(
            `All hire stock bulk items deleted successfully (${response.data.deletedCount} records)`
          );
          setSelectedCategory('');
          fetchHireStockBulk(1, '', '');
        } catch (error) {
          setError('Failed to delete all hire stock bulk items');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-boxes me-3 text-info" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Hire Stock Bulk</h1>
              <p className="text-muted mb-0">Manage bulk rental equipment and inventory items</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddHireStockBulk} className="me-2">
            Add Hire Stock Bulk
          </Button>
          <Button variant="primary" onClick={handleApplyRateClick} className="me-2">
            Apply Rate
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllHireStockBulk}
            disabled={hireStockBulk.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {copyProgress && (
        <Row className="mb-3">
          <Col>
            <OperationProgress
              title={copyProgress.title}
              processed={copyProgress.processed}
              total={copyProgress.total}
              success={copyProgress.success}
              failed={copyProgress.failed}
              variant="info"
            />
          </Col>
        </Row>
      )}

      <Row>
        {/* Category Filter Sidebar */}
        {hireCategories.length > 0 && (
          <Col md={2} className="pe-0">
            <Card className="h-100">
              <Card.Header className="py-2">
                <small className="fw-bold">Categories</small>
              </Card.Header>
              <div
                className="list-group list-group-flush thin-scrollbar"
                style={{
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#ccc transparent',
                }}
              >
                <button
                  type="button"
                  className={`list-group-item list-group-item-action py-2 ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('')}
                >
                  <small>All Categories</small>
                </button>
                {hireCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={`list-group-item list-group-item-action py-2 ${selectedCategory === category.code ? 'active' : ''}`}
                    onClick={() => handleCategoryFilter(category.code)}
                  >
                    <small>{category.name}</small>
                  </button>
                ))}
              </div>
            </Card>
          </Col>
        )}

        <Col md={hireCategories.length > 0 ? 10 : 12}>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSearch} className="mb-3">
                <Row>
                  <Col>
                    <Form.Control
                      type="text"
                      placeholder="Search hire stock bulk..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" type="submit">
                      Search
                    </Button>
                  </Col>
                </Row>
              </Form>

              {loading ? (
                <div className="text-center p-3">Loading hire stock bulk...</div>
              ) : hireStockBulk.length === 0 ? (
                <div className="text-center p-3">No hire stock bulk items found.</div>
              ) : (
                <>
                  <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                    <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Stock No</th>
                          <th>Name</th>
                          <th>Sale Price</th>
                          <th>Week Rate</th>
                          <th>Day Rate</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hireStockBulk.map((item) => (
                          <tr key={item.id}>
                            <td>{getCategoryName(item.cat)}</td>
                            <td>{item.stock_no}</td>
                            <td>{item.name}</td>
                            <td>
                              {item.sale_price ? `£${parseFloat(item.sale_price).toFixed(2)}` : ''}
                            </td>
                            <td>
                              {item.week_rate ? `£${parseFloat(item.week_rate).toFixed(2)}` : ''}
                            </td>
                            <td>
                              {item.day_rate ? `£${parseFloat(item.day_rate).toFixed(2)}` : ''}
                            </td>
                            <td>
                              <Badge bg={item.active ? 'success' : 'secondary'}>
                                {item.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditHireStockBulk(item)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleCopyHireStockBulk(item)}
                                  disabled={copyingId === item.id}
                                >
                                  {copyingId === item.id ? 'Copying...' : 'Copy'}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteHireStockBulk(item.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <GridPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showModal && (
        <HireStockBulkModal
          hireStockBulk={editingHireStockBulk}
          onClose={() => setShowModal(false)}
          onSave={handleSaveHireStockBulk}
          defaultCategory={selectedCategory}
        />
      )}

      <ApplyRateModal
        show={showApplyRateModal}
        onClose={() => setShowApplyRateModal(false)}
        items={applyRateItems}
        hireCategories={hireCategories}
        onApplied={(count) => {
          setSuccess(`Rates applied to ${count} item(s) successfully`);
          fetchHireStockBulk(pagination.page, searchTerm, selectedCategory);
        }}
        onRefetchItems={async (categoryCode) => {
          const res = await axios.get('/api/hire-stock-bulk', {
            params: { page: 1, limit: 10000, category: categoryCode },
          });
          setApplyRateItems(res.data.hireStockBulk || []);
        }}
        apiPath="/api/hire-stock-bulk"
        categoryName={selectedCategory ? getCategoryName(selectedCategory) : null}
      />
    </Container>
  );
}

export default HireStockBulk;
